export const M6_UNIT_13 = {
  id: "m6-u13-countries-locations",
  title: "Unidad 13: Countries, Nationalities & Where Things Are",
  topic: "Master countries, nationalities, and how to describe where places are located. Essential for travel, meeting new people, and understanding geography in any English-speaking country! 🌍",
  iconType: "globe",
  grammarImage: "grammar/grammar-m6-u13.jpg",
  grammarText: "Use country names without 'the' (Mexico, not the Mexico). Learn prepositions: IN the north, ON the coast, BY the sea.",
  
  sets: [
    // ============================================
    // SETS 1-2: COUNTRIES VOCABULARY
    // ============================================
    {
      id: "m6-u13-s1",
      title: "Set 1: Countries Around the World",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "Which country is in North America?",
          options: ["Brazil", "Canada", "Spain"],
          a: "Canada",
          tip: "North America has three main countries: Canada, USA, and Mexico!",
          successtip: "✓ CANADA! 🍁 Second largest country in the world by area! English and French official languages!"
        },
        {
          type: "mc",
          q: "Which country is in South America?",
          options: ["Portugal", "Argentina", "Italy"],
          a: "Argentina",
          tip: "South America is below Central America and Mexico!",
          successtip: "✓ ARGENTINA! 🇦🇷 Tango, football legends like Messi, and beautiful landscapes from Patagonia to Iguazu Falls!"
        },
        {
          type: "input",
          q: "Complete: I want to visit ___. (country with Eiffel Tower)",
          a: ["France", "france"],
          tip: "The Eiffel Tower is in Paris, the capital city!",
          successtip: "✓ FRANCE! 🗼 Most visited country in the world! Over 89 million tourists every year!"
        },
        {
          type: "mc",
          q: "Which country is in Asia?",
          options: ["Egypt", "Thailand", "Greece"],
          a: "Thailand",
          tip: "Asia is the largest continent with 48 countries!",
          successtip: "✓ THAILAND! 🇹🇭 Known as 'Land of Smiles'! Amazing food, temples, and beaches worldwide!"
        },
        {
          type: "order",
          q: "Traduce: México es un país hermoso.",
          words: ["Mexico", "is", "a", "beautiful", "country"],
          a: "Mexico is a beautiful country",
          tip: "No article before country names! Mexico NOT the Mexico!",
          successtip: "✓ NO THE! Country names stand alone: Mexico, Canada, Brazil! Exception: THE United States, THE United Kingdom!"
        },
        {
          type: "mc",
          q: "Which country is in Europe?",
          options: ["Australia", "Germany", "India"],
          a: "Germany",
          tip: "Europe has 44 countries, from Portugal to Russia!",
          successtip: "✓ GERMANY! 🇩🇪 Largest economy in Europe! Known for engineering, cars, and Oktoberfest worldwide!"
        },
        {
          type: "input",
          q: "Complete: I'm from ___. (country with pyramids)",
          a: ["Egypt", "egypt"],
          tip: "Ancient pyramids are in this African country!",
          successtip: "✓ EGYPT! 🏜️ Home to Pyramids of Giza, one of the Seven Wonders! Rich history spanning 5,000 years!"
        },
        {
          type: "mc",
          q: "Which country is NOT in Africa?",
          options: ["Nigeria", "Pakistan", "South Africa"],
          a: "Pakistan",
          tip: "Pakistan is in South Asia, next to India!",
          successtip: "✓ PAKISTAN! 🇵🇰 In Asia! Africa has 54 countries, more than any other continent!"
        },
        {
          type: "order",
          q: "Traduce: China es muy grande.",
          words: ["China", "is", "very", "big"],
          a: "China is very big",
          tip: "Very = muy! Big = grande! Simple present for facts!",
          successtip: "✓ CHINA! 🇨🇳 Most populous country! Over 1.4 billion people! Ancient culture and modern technology!"
        },
        {
          type: "input",
          q: "Complete: The capital of Japan is ___.",
          a: ["Tokyo", "tokyo"],
          tip: "This city hosted the Olympics in 2020 (held in 2021)!",
          successtip: "✓ TOKYO! 🗾 Largest metropolitan area in the world! Over 37 million people! Tech capital globally!"
        }
      ]
    },
    
    {
      id: "m6-u13-s2",
      title: "Set 2: More Countries",
      category: "vocabulary",
      exercises: [
        {
          type: "match",
          q: "Match the country to its continent:",
          pairs: [
            { left: "Brazil", right: "South America" },
            { left: "Australia", right: "Oceania" },
            { left: "Russia", right: "Europe and Asia" }
          ],
          a: "completed",
          successtip: "✓ CONTINENTS! 🌏 Russia spans TWO continents! Largest country by area in the world!"
        },
        {
          type: "mc",
          q: "Which country is in Oceania?",
          options: ["New Zealand", "Philippines", "Indonesia"],
          a: "New Zealand",
          tip: "Oceania includes Australia, New Zealand, and Pacific islands!",
          successtip: "✓ NEW ZEALAND! 🇳🇿 Stunning nature! Home to Maori culture and filming location for Lord of the Rings!"
        },
        {
          type: "input",
          q: "Complete: I live in ___. (country with Big Ben)",
          a: ["United Kingdom", "the United Kingdom", "UK", "the UK", "England", "Britain"],
          tip: "Big Ben is in London! Country needs THE before it!",
          successtip: "✓ UNITED KINGDOM! 🇬🇧 Four countries: England, Scotland, Wales, Northern Ireland! English spoken worldwide!"
        },
        {
          type: "order",
          q: "Traduce: España está en Europa.",
          words: ["Spain", "is", "in", "Europe"],
          a: "Spain is in Europe",
          tip: "Use IN with continents and countries!",
          successtip: "✓ SPAIN! 🇪🇸 Beautiful beaches, flamenco, paella! Spanish spoken in 20+ countries worldwide!"
        },
        {
          type: "mc",
          q: "The United Arab Emirates is in:",
          options: ["Africa", "Middle East", "South Asia"],
          a: "Middle East",
          tip: "UAE is on the Arabian Peninsula in Western Asia!",
          successtip: "✓ UAE! 🇦🇪 Dubai and Abu Dhabi! Modern cities in the desert! International business hub!"
        },
        {
          type: "input",
          q: "Complete: The capital of Italy is ___.",
          a: ["Rome", "rome"],
          tip: "This ancient city has the Colosseum!",
          successtip: "✓ ROME! 🇮🇹 Eternal City! Vatican City inside Rome! Rich history, art, and amazing food!"
        },
        {
          type: "mc",
          q: "Which country is the largest by area?",
          options: ["Canada", "China", "Russia"],
          a: "Russia",
          tip: "This country spans 11 time zones!",
          successtip: "✓ RUSSIA! 🇷🇺 17 million square kilometers! Spans Europe and Asia! Diverse landscapes and climates!"
        },
        {
          type: "order",
          q: "Traduce: India tiene muchas personas.",
          words: ["India", "has", "many", "people"],
          a: "India has many people",
          tip: "Has = tiene (for he/she/it and countries)! Many = muchas/muchos!",
          successtip: "✓ INDIA! 🇮🇳 Most populous country now! Over 1.4 billion people! Incredible diversity and culture!"
        },
        {
          type: "input",
          q: "Complete: I want to visit ___. (country with kangaroos)",
          a: ["Australia", "australia"],
          tip: "This country is also a continent!",
          successtip: "✓ AUSTRALIA! 🦘 Unique wildlife! Great Barrier Reef! Sydney Opera House! English-speaking country!"
        },
        {
          type: "mc",
          q: "Which country is in Central America?",
          options: ["Costa Rica", "Colombia", "Cuba"],
          a: "Costa Rica",
          tip: "Central America connects North and South America!",
          successtip: "✓ COSTA RICA! 🇨🇷 Pura vida! Beautiful nature, beaches, and rainforests! Popular for eco-tourism!"
        }
      ]
    },

    // ============================================
    // SETS 3-4: NATIONALITIES
    // ============================================
    {
      id: "m6-u13-s3",
      title: "Set 3: Nationalities",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "I'm from Mexico. I'm ___.",
          options: ["Mexican", "Mexicano", "Mexico"],
          a: "Mexican",
          tip: "Nationality is an adjective in English! I'm Mexican NOT I'm mexicano!",
          successtip: "✓ MEXICAN! 🇲🇽 Nationality adjective! NOT mexicano in English! Always capital M!"
        },
        {
          type: "order",
          q: "Traduce: Ella es canadiense.",
          words: ["She", "is", "Canadian"],
          a: "She is Canadian",
          tip: "Canadian = canadiense! Always capitalize nationalities in English!",
          successtip: "✓ CANADIAN! 🍁 Nationality adjectives always start with capital letter in English! Canadian, Mexican, Chinese!"
        },
        {
          type: "input",
          q: "Complete: He's from Brazil. He's ___.",
          a: ["Brazilian", "brazilian"],
          tip: "Brazil → Brazilian! Add -ian to make nationality!",
          successtip: "✓ BRAZILIAN! 🇧🇷 Pattern: Brazil → Brazilian! Egypt → Egyptian! Italy → Italian!"
        },
        {
          type: "mc",
          q: "She's from China. She's ___.",
          options: ["Chinesa", "Chinese", "China"],
          a: "Chinese",
          tip: "China → Chinese! NOT chinesa!",
          successtip: "✓ CHINESE! 🇨🇳 Mandarin Chinese most spoken language globally! Over 1 billion native speakers!"
        },
        {
          type: "match",
          q: "Match the country to the nationality:",
          pairs: [
            { left: "France", right: "French" },
            { left: "Japan", right: "Japanese" },
            { left: "Spain", right: "Spanish" }
          ],
          a: "completed",
          successtip: "✓ PATTERNS! France → French! Japan → Japanese! Spain → Spanish! Each country has its own pattern!"
        },
        {
          type: "input",
          q: "Complete: I'm from the United States. I'm ___.",
          a: ["American", "american"],
          tip: "United States → American! USA = America in nationality!",
          successtip: "✓ AMERICAN! 🇺🇸 American can mean from USA or from Americas (North/South)! Context matters!"
        },
        {
          type: "order",
          q: "Traduce: Somos argentinos.",
          words: ["We", "are", "Argentine", "Argentinian"],
          a: "We are Argentine",
          tip: "Both Argentine and Argentinian are correct!",
          successtip: "✓ ARGENTINE! 🇦🇷 Both forms accepted! Argentine or Argentinian! Same with Philippines → Philippine/Filipino!"
        },
        {
          type: "mc",
          q: "They're from Germany. They're ___.",
          options: ["Germany", "German", "Germanic"],
          a: "German",
          tip: "Germany → German! Remove the -y and add -an!",
          successtip: "✓ GERMAN! 🇩🇪 Pattern: Germany → German! Norway → Norwegian! Hungary → Hungarian!"
        },
        {
          type: "input",
          q: "Complete: She's from India. She's ___.",
          a: ["Indian", "indian"],
          tip: "India → Indian! Simple -n ending!",
          successtip: "✓ INDIAN! 🇮🇳 Over 1,600 languages spoken in India! English widely used for business!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: I'm from the Mexico. ¿Correcto?",
          options: ["Yes, correct", "No, I'm from Mexico", "No, I from Mexico"],
          a: "No, I'm from Mexico",
          tip: "NO article before most countries! Mexico NOT the Mexico!",
          successtip: "✓ NO THE! Most countries no article: Mexico, Canada, France! Exceptions: THE United States, THE United Kingdom!"
        }
      ]
    },

    {
      id: "m6-u13-s4",
      title: "Set 4: More Nationalities",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "He's from England. He's ___.",
          options: ["England", "Englander", "English"],
          a: "English",
          tip: "England → English! Also applies to language: I speak English!",
          successtip: "✓ ENGLISH! 🏴󠁧󠁢󠁥󠁮󠁧󠁿 Language and nationality! English spoken by 1.5 billion people worldwide!"
        },
        {
          type: "input",
          q: "Complete: We're from Australia. We're ___.",
          a: ["Australian", "australian"],
          tip: "Australia → Australian! Add -n!",
          successtip: "✓ AUSTRALIAN! 🇦🇺 Aussies! Unique accent and slang! G'day mate! English-speaking country!"
        },
        {
          type: "order",
          q: "Traduce: Él es italiano.",
          words: ["He", "is", "Italian"],
          a: "He is Italian",
          tip: "Italiano = Italian in English! Capital I!",
          successtip: "✓ ITALIAN! 🇮🇹 Italy → Italian! Beautiful language and culture! Italian food loved worldwide!"
        },
        {
          type: "match",
          q: "Match the country to the nationality:",
          pairs: [
            { left: "Poland", right: "Polish" },
            { left: "Turkey", right: "Turkish" },
            { left: "Thailand", right: "Thai" }
          ],
          a: "completed",
          successtip: "✓ PATTERNS! Poland → Polish! Turkey → Turkish! Thailand → Thai! Each unique!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: I'm Mexican people. ¿Correcto?",
          options: ["Yes, correct", "No, I'm Mexican", "No, I'm a Mexican"],
          a: "No, I'm Mexican",
          tip: "Mexican is already an adjective! I'm Mexican NOT I'm Mexican people!",
          successtip: "✓ ADJECTIVE! Nationality IS the adjective! I'm Mexican (not people)! I'm Canadian! I'm Brazilian!"
        },
        {
          type: "input",
          q: "Complete: They're from South Korea. They're ___.",
          a: ["Korean", "korean", "South Korean", "south korean"],
          tip: "Korea → Korean! Both South Korean and Korean accepted!",
          successtip: "✓ KOREAN! 🇰🇷 K-pop, technology, amazing food! Korean culture popular globally!"
        },
        {
          type: "order",
          q: "Traduce: Ella es portuguesa.",
          words: ["She", "is", "Portuguese"],
          a: "She is Portuguese",
          tip: "Portuguesa = Portuguese! Same for both genders in English!",
          successtip: "✓ PORTUGUESE! 🇵🇹 Portugal → Portuguese! Language spoken in Brazil, Portugal, Angola, Mozambique!"
        },
        {
          type: "mc",
          q: "I'm from Russia. I'm ___.",
          options: ["Russ", "Russian", "Rusian"],
          a: "Russian",
          tip: "Russia → Russian! Add -n!",
          successtip: "✓ RUSSIAN! 🇷🇺 Russian language uses Cyrillic alphabet! Largest country spans 11 time zones!"
        },
        {
          type: "input",
          q: "Complete: He's from Egypt. He's ___.",
          a: ["Egyptian", "egyptian"],
          tip: "Egypt → Egyptian! Pattern: -ian ending!",
          successtip: "✓ EGYPTIAN! 🇪🇬 Ancient civilization! Arabic spoken today! English widely used in tourism!"
        },
        {
          type: "mc",
          q: "We're from the Philippines. We're ___.",
          options: ["Philippines", "Filipino", "Philippino"],
          a: "Filipino",
          tip: "Philippines → Filipino! Unique pattern!",
          successtip: "✓ FILIPINO! 🇵🇭 Over 7,000 islands! English and Filipino official languages! Friendly culture!"
        }
      ]
    },

    // ============================================
    // SETS 5-6: BASIC PREPOSITIONS
    // ============================================
    {
      id: "m6-u13-s5",
      title: "Set 5: Where Things Are - Basic",
      category: "grammar",
      exercises: [
        {
          type: "mc",
          q: "I live ___ Canada.",
          options: ["in", "on", "at"],
          a: "in",
          tip: "Use IN with countries and cities!",
          successtip: "✓ IN! Use IN for countries, cities, and continents! IN Mexico, IN Tokyo, IN Europe!"
        },
        {
          type: "order",
          q: "Traduce: Vivo en la costa.",
          words: ["I", "live", "on", "the", "coast"],
          a: "I live on the coast",
          tip: "ON the coast! NOT in coast! Preposition matters!",
          successtip: "✓ ON THE COAST! 🌊 Correct preposition! ON the coast means near the ocean!"
        },
        {
          type: "input",
          q: "Complete: My house is ___ the sea. (cerca del mar)",
          a: ["by", "near"],
          tip: "BY the sea = near the sea! Both correct!",
          successtip: "✓ BY THE SEA! 🌊 BY or NEAR both work! By the sea = close to the ocean!"
        },
        {
          type: "mc",
          q: "They live ___ an island.",
          options: ["in", "on", "at"],
          a: "on",
          tip: "Use ON with islands! ON an island, ON Tenerife!",
          successtip: "✓ ON! 🏝️ Use ON with islands! ON Cuba, ON Jamaica, ON an island!"
        },
        {
          type: "match",
          q: "Match the place to the correct preposition:",
          pairs: [
            { left: "in", right: "the city" },
            { left: "on", right: "the coast" },
            { left: "by", right: "the sea" }
          ],
          a: "completed",
          successtip: "✓ PREPOSITIONS! IN city, ON coast, BY sea! Each preposition has specific uses!"
        },
        {
          type: "order",
          q: "Traduce: El hotel está en las montañas.",
          words: ["The", "hotel", "is", "in", "the", "mountains"],
          a: "The hotel is in the mountains",
          tip: "IN the mountains! Mountains = montañas!",
          successtip: "✓ IN THE MOUNTAINS! ⛰️ Use IN for mountainous areas! Beautiful landscapes!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: I live in coast. ¿Correcto?",
          options: ["Yes, correct", "No, I live on the coast", "No, I live at coast"],
          a: "No, I live on the coast",
          tip: "ON the coast! Need article THE! Not just 'in coast'!",
          successtip: "✓ ON THE COAST! Two changes: ON (not in) and THE (article needed)!"
        },
        {
          type: "input",
          q: "Complete: Tokyo is ___ Japan.",
          a: ["in"],
          tip: "Cities are IN countries! Tokyo IN Japan!",
          successtip: "✓ IN! 🗾 Cities IN countries! Paris IN France, London IN England, Tokyo IN Japan!"
        },
        {
          type: "mc",
          q: "The restaurant is ___ the river.",
          options: ["in", "on", "at"],
          a: "on",
          tip: "ON the river = next to the river!",
          successtip: "✓ ON THE RIVER! 🏞️ ON means beside or next to! Beautiful location for dining!"
        },
        {
          type: "input",
          q: "Complete: We stay ___ a hotel. (preposition)",
          a: ["in", "at"],
          tip: "IN a hotel or AT a hotel! Both common!",
          successtip: "✓ IN/AT! Both accepted! IN emphasizes inside, AT emphasizes location! Regional preference!"
        }
      ]
    },

    {
      id: "m6-u13-s6",
      title: "Set 6: Forests, Cities & Rivers",
      category: "grammar",
      exercises: [
        {
          type: "mc",
          q: "The house is ___ the forest.",
          options: ["in", "on", "at"],
          a: "in",
          tip: "IN the forest! Surrounded by trees!",
          successtip: "✓ IN THE FOREST! 🌲 IN means surrounded! Beautiful natural setting!"
        },
        {
          type: "order",
          q: "Traduce: Vivo en la ciudad.",
          words: ["I", "live", "in", "the", "city"],
          a: "I live in the city",
          tip: "IN the city! Cities use IN preposition!",
          successtip: "✓ IN THE CITY! 🏙️ Urban life! IN for cities: IN New York, IN Mexico City, IN Tokyo!"
        },
        {
          type: "input",
          q: "Complete: Paris is ___ the Seine River. (junto al río)",
          a: ["on"],
          tip: "ON the river! Cities next to rivers use ON!",
          successtip: "✓ ON THE RIVER! Many famous cities ON rivers! London on Thames, Paris on Seine!"
        },
        {
          type: "mc",
          q: "My town is ___ the mountains.",
          options: ["in", "on", "at"],
          a: "in",
          tip: "IN the mountains! Surrounded by mountains!",
          successtip: "✓ IN THE MOUNTAINS! ⛰️ Mountain towns beautiful worldwide! Fresh air and views!"
        },
        {
          type: "match",
          q: "Match the phrase:",
          pairs: [
            { left: "in the forest", right: "en el bosque" },
            { left: "on the river", right: "junto al río" },
            { left: "in the city", right: "en la ciudad" }
          ],
          a: "completed",
          successtip: "✓ LOCATIONS! Different prepositions create different meanings! Master these patterns!"
        },
        {
          type: "order",
          q: "Traduce: El bosque está cerca del mar.",
          words: ["The", "forest", "is", "near", "the", "sea"],
          a: "The forest is near the sea",
          tip: "NEAR = cerca de! Simple and clear!",
          successtip: "✓ NEAR! Versatile word for proximity! NEAR the sea, NEAR the city, NEAR home!"
        },
        {
          type: "mc",
          q: "The hotel is ___ the coast ___ the sea.",
          options: ["on / by", "in / on", "at / in"],
          a: "on / by",
          tip: "ON the coast and BY the sea! Two prepositions needed!",
          successtip: "✓ ON THE COAST BY THE SEA! 🌊 Perfect location! Both prepositions describe coastal position!"
        },
        {
          type: "input",
          q: "Complete: We live ___ a small town ___ the mountains.",
          a: ["in, in", "in in"],
          tip: "IN a town and IN the mountains! Both use IN!",
          successtip: "✓ IN! Both need IN! Small town life in mountains peaceful worldwide!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: The city is in the river. ¿Correcto?",
          options: ["Yes, correct", "No, the city is on the river", "No, the city is at the river"],
          a: "No, the city is on the river",
          tip: "ON the river! IN would mean underwater! ON = next to!",
          successtip: "✓ ON THE RIVER! IN means inside/underwater! ON means beside! Big difference!"
        },
        {
          type: "input",
          q: "Complete: The Amazon rainforest is ___ South America.",
          a: ["in"],
          tip: "IN South America! Continent uses IN!",
          successtip: "✓ AMAZON! 🌳 Largest rainforest in the world! IN South America spans multiple countries!"
        }
      ]
    },

    // ============================================
    // SETS 7-8: ADVANCED PREPOSITIONS
    // ============================================
    {
      id: "m6-u13-s7",
      title: "Set 7: Islands & Advanced Locations",
      category: "grammar",
      exercises: [
        {
          type: "mc",
          q: "Tenerife is an island ___ the coast of Africa.",
          options: ["off", "of", "in"],
          a: "off",
          tip: "OFF the coast = separated from the coast by water!",
          successtip: "✓ OFF THE COAST! 🏝️ OFF means separated by water! Important for islands!"
        },
        {
          type: "order",
          q: "Traduce: Vivo en una isla.",
          words: ["I", "live", "on", "an", "island"],
          a: "I live on an island",
          tip: "ON an island! Not IN! Islands use ON!",
          successtip: "✓ ON AN ISLAND! 🌴 Island life special! Use ON: ON Cuba, ON Jamaica, ON an island!"
        },
        {
          type: "input",
          q: "Complete: Cuba is ___ island ___ the Caribbean Sea.",
          a: ["an, in", "an in"],
          tip: "AN island (vowel sound) and IN the Caribbean!",
          successtip: "✓ CUBA! 🇨🇺 Beautiful island nation! IN the Caribbean! Music, culture, beaches!"
        },
        {
          type: "mc",
          q: "The Galapagos Islands are ___ the coast ___ Ecuador.",
          options: ["off / of", "on / in", "in / on"],
          a: "off / of",
          tip: "OFF the coast OF Ecuador! Two prepositions needed!",
          successtip: "✓ GALAPAGOS! 🐢 Famous for unique wildlife! OFF means away from! Darwin studied there!"
        },
        {
          type: "match",
          q: "Match the location phrases:",
          pairs: [
            { left: "on an island", right: "en una isla" },
            { left: "off the coast", right: "frente a la costa" },
            { left: "in the Caribbean", right: "en el Caribe" }
          ],
          a: "completed",
          successtip: "✓ ISLAND PHRASES! These patterns describe islands worldwide! Very useful for geography!"
        },
        {
          type: "order",
          q: "Traduce: La isla está cerca de la costa.",
          words: ["The", "island", "is", "near", "the", "coast"],
          a: "The island is near the coast",
          tip: "NEAR = cerca de! Simple and clear!",
          successtip: "✓ NEAR THE COAST! Close but not touching! Some islands very close to mainland!"
        },
        {
          type: "mc",
          q: "Hawaii is ___ the Pacific Ocean.",
          options: ["in", "on", "at"],
          a: "in",
          tip: "IN the ocean! Surrounded by water!",
          successtip: "✓ HAWAII! 🌺 Beautiful US state IN the Pacific! 8 main islands! Aloha spirit!"
        },
        {
          type: "input",
          q: "Complete: Madagascar is a large island ___ the coast ___ Africa.",
          a: ["off, of", "off of"],
          tip: "OFF the coast OF Africa! Island separated from continent!",
          successtip: "✓ MADAGASCAR! 🇲🇬 4th largest island in the world! Unique wildlife! 90% species found nowhere else!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: I live in an island. ¿Correcto?",
          options: ["Yes, correct", "No, I live on an island", "No, I live at an island"],
          a: "No, I live on an island",
          tip: "ON an island! Not IN! Islands are surfaces, use ON!",
          successtip: "✓ ON AN ISLAND! Think of surface! ON shows you're on top of the land!"
        },
        {
          type: "input",
          q: "Complete: The hotel is right ___ the beach. (justo junto)",
          a: ["next to", "by", "beside", "near"],
          tip: "RIGHT next to = very close! Multiple options work!",
          successtip: "✓ NEXT TO! RIGHT emphasizes very close! Next to, by, beside all mean close proximity!"
        }
      ]
    },

    {
      id: "m6-u13-s8",
      title: "Set 8: Precise Location Phrases",
      category: "grammar",
      exercises: [
        {
          type: "mc",
          q: "The bank is ___ the hospital. (justo al lado)",
          options: ["right next to", "far from", "opposite"],
          a: "right next to",
          tip: "RIGHT next to = immediately beside! Very close!",
          successtip: "✓ RIGHT NEXT TO! Emphasizes proximity! RIGHT makes it stronger than just 'next to'!"
        },
        {
          type: "order",
          q: "Traduce: El museo está enfrente del parque.",
          words: ["The", "museum", "is", "opposite", "the", "park"],
          a: "The museum is opposite the park",
          tip: "OPPOSITE = enfrente de! Facing each other!",
          successtip: "✓ OPPOSITE! 🏛️ Across from each other! Also: directly opposite, diagonally opposite!"
        },
        {
          type: "input",
          q: "Complete: The café is ___ the two stores. (entre)",
          a: ["between"],
          tip: "BETWEEN = entre (two things)! Entre dos cosas!",
          successtip: "✓ BETWEEN! For two things! AMONG for more than two! Between shops, among friends!"
        },
        {
          type: "mc",
          q: "The restaurant is halfway ___ my house and work.",
          options: ["between", "among", "next"],
          a: "between",
          tip: "HALFWAY between = a medio camino entre!",
          successtip: "✓ HALFWAY BETWEEN! Perfect middle point! Convenient location!"
        },
        {
          type: "match",
          q: "Match the precise location phrases:",
          pairs: [
            { left: "right next to", right: "justo al lado" },
            { left: "opposite", right: "enfrente de" },
            { left: "between", right: "entre (dos)" }
          ],
          a: "completed",
          successtip: "✓ PRECISE PHRASES! These give exact locations! Very useful for directions!"
        },
        {
          type: "order",
          q: "Traduce: El hotel está a la izquierda del banco.",
          words: ["The", "hotel", "is", "to", "the", "left", "of", "the", "bank"],
          a: "The hotel is to the left of the bank",
          tip: "TO THE LEFT OF = a la izquierda de! Complete phrase!",
          successtip: "✓ TO THE LEFT! Also: to the right, just to the left, directly to the right!"
        },
        {
          type: "mc",
          q: "The store is ___ the castle. (enfrente, diagonal)",
          options: ["diagonally opposite", "halfway between", "right next to"],
          a: "diagonally opposite",
          tip: "DIAGONALLY opposite = en diagonal enfrente!",
          successtip: "✓ DIAGONALLY OPPOSITE! More specific than just 'opposite'! Diagonal position!"
        },
        {
          type: "input",
          q: "Complete: My office is directly ___ the station. (enfrente)",
          a: ["opposite"],
          tip: "DIRECTLY opposite = exactly across! Very clear location!",
          successtip: "✓ DIRECTLY OPPOSITE! DIRECTLY makes it more precise! Exactly facing each other!"
        },
        {
          type: "mc",
          q: "The park is ___ the library. (delante de)",
          options: ["in front of", "behind", "inside"],
          a: "in front of",
          tip: "IN FRONT OF = delante de! BEHIND = detrás de!",
          successtip: "✓ IN FRONT OF! Opposite is BEHIND! Useful for all directions and locations!"
        },
        {
          type: "input",
          q: "Complete: The gym is ___ my house. (cerca de)",
          a: ["near", "close to", "by", "next to"],
          tip: "NEAR, CLOSE TO, BY all mean cerca de! Multiple options!",
          successtip: "✓ NEAR! Many ways to express proximity! Near, close to, by, next to all work!"
        }
      ]
    },

    // ============================================
    // SETS 9-10: COMPASS POINTS & COMPLEX LOCATIONS
    // ============================================
    {
      id: "m6-u13-s9",
      title: "Set 9: Compass Points",
      category: "vocabulary",
      exercises: [
        {
          type: "match",
          q: "Match the compass points:",
          pairs: [
            { left: "north", right: "norte" },
            { left: "south", right: "sur" },
            { left: "east", right: "este" },
            { left: "west", right: "oeste" }
          ],
          a: "completed",
          successtip: "✓ COMPASS! 🧭 North, South, East, West! Essential for directions worldwide!"
        },
        {
          type: "mc",
          q: "Canada is ___ the north of the United States.",
          options: ["in", "to", "at"],
          a: "in",
          tip: "IN the north = en el norte! Position within direction!",
          successtip: "✓ IN THE NORTH! 🍁 Canada IN the north of North America! Cold winters, beautiful nature!"
        },
        {
          type: "order",
          q: "Traduce: México está en el sur de Norteamérica.",
          words: ["Mexico", "is", "in", "the", "south", "of", "North", "America"],
          a: "Mexico is in the south of North America",
          tip: "IN the south! Direction needs IN!",
          successtip: "✓ MEXICO! 🇲🇽 IN the south connects USA and Central America! Rich culture and history!"
        },
        {
          type: "input",
          q: "Complete: Japan is ___ the east of Asia.",
          a: ["in", "to"],
          tip: "IN the east or TO the east! Both work!",
          successtip: "✓ JAPAN! 🇯🇵 Island nation IN the east of Asia! Land of the Rising Sun!"
        },
        {
          type: "mc",
          q: "Spain is in the ___ of Europe.",
          options: ["south", "north", "east"],
          a: "south",
          tip: "Spain is southern Europe! Mediterranean climate!",
          successtip: "✓ SOUTH! 🇪🇸 Spain IN the south of Europe! Warm weather, beaches, great food!"
        },
        {
          type: "order",
          q: "Traduce: Australia está en el sur.",
          words: ["Australia", "is", "in", "the", "south"],
          a: "Australia is in the south",
          tip: "IN the south! Southern hemisphere!",
          successtip: "✓ AUSTRALIA! 🇦🇺 IN the south means southern hemisphere! Seasons opposite to north!"
        },
        {
          type: "mc",
          q: "The UK is in the ___ of Europe.",
          options: ["west", "east", "south"],
          a: "west",
          tip: "UK is western Europe! Atlantic Ocean side!",
          successtip: "✓ WEST! 🇬🇧 UK IN the west of Europe! Island nation! Close to Ireland!"
        },
        {
          type: "input",
          q: "Complete: China is in ___ Asia. (direction)",
          a: ["east", "the east of", "eastern"],
          tip: "EAST Asia or EASTERN Asia! Region name!",
          successtip: "✓ EAST ASIA! 🌏 Region includes China, Japan, Korea! Major economic zone!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: Mexico is to south. ¿Correcto?",
          options: ["Yes, correct", "No, in the south", "No, at south"],
          a: "No, in the south",
          tip: "IN the south! Need THE article! Not just 'to south'!",
          successtip: "✓ IN THE SOUTH! Need article THE with directions! IN the north, IN the south!"
        },
        {
          type: "input",
          q: "Complete: Norway is in the ___ of Europe. (direction: norte)",
          a: ["north"],
          tip: "NORTH = norte! Cold climate!",
          successtip: "✓ NORTH! 🇳🇴 Norway IN the north! Scandinavia! Beautiful fjords and northern lights!"
        }
      ]
    },

    {
      id: "m6-u13-s10",
      title: "Set 10: Integration & Real Context",
      category: "integration",
      exercises: [
        {
          type: "mc",
          q: "I'm Mexican. I'm from Mexico. Mexico is ___ North America.",
          options: ["in", "on", "at"],
          a: "in",
          tip: "IN North America! Continent uses IN!",
          successtip: "✓ PERFECT! 🇲🇽 Nationality + country + location! Complete information!"
        },
        {
          type: "order",
          q: "Traduce: Soy de Brasil. Brasil está en Sudamérica.",
          words: ["I'm", "from", "Brazil", "Brazil", "is", "in", "South", "America"],
          a: "I'm from Brazil Brazil is in South America",
          tip: "Two sentences! I'm from + Brazil is in!",
          successtip: "✓ COMPLETE INFO! 🇧🇷 Origin and location! Useful for introductions!"
        },
        {
          type: "input",
          q: "Complete: I'm ___. I live in Tokyo. Tokyo is ___ Japan.",
          a: ["Japanese, in", "japanese, in", "Japanese in"],
          tip: "Nationality + IN Japan! Cities IN countries!",
          successtip: "✓ COMPLETE! Nationality, city, and country! Perfect introduction pattern!"
        },
        {
          type: "mc",
          q: "She's Canadian. She lives in Vancouver. Vancouver is on the ___ coast of Canada.",
          options: ["west", "east", "south"],
          a: "west",
          tip: "Vancouver is western Canada! Pacific Ocean!",
          successtip: "✓ VANCOUVER! 🇨🇦 Beautiful city ON the west coast! Mountains and ocean!"
        },
        {
          type: "match",
          q: "Match to create complete location descriptions:",
          pairs: [
            { left: "I'm from Cuba, an island", right: "in the Caribbean" },
            { left: "I live in Sydney, on", right: "the coast of Australia" },
            { left: "My town is in the mountains", right: "in the north of Italy" }
          ],
          a: "completed",
          successtip: "✓ COMPLETE DESCRIPTIONS! 🌍 Combining all location phrases! Real-world patterns!"
        },
        {
          type: "order",
          q: "Traduce: Vivo en una ciudad en la costa sur.",
          words: ["I", "live", "in", "a", "city", "on", "the", "south", "coast"],
          a: "I live in a city on the south coast",
          tip: "IN a city, ON the coast! Two prepositions!",
          successtip: "✓ TWO PREPOSITIONS! IN for city, ON for coast! Coastal city life beautiful!"
        },
        {
          type: "mc",
          q: "He's from Thailand. Thailand is in ___ Asia, by the sea.",
          options: ["Southeast", "Northeast", "Northwest"],
          a: "Southeast",
          tip: "Southeast Asia! Tropical region!",
          successtip: "✓ SOUTHEAST ASIA! 🇹🇭 Region includes Thailand, Vietnam, Philippines! Beautiful beaches!"
        },
        {
          type: "input",
          q: "Complete: I'm Italian. I'm from Rome. Rome is ___ the center of Italy.",
          a: ["in"],
          tip: "IN the center! Central location!",
          successtip: "✓ ROME! 🇮🇹 Capital IN the center! Ancient history, beautiful architecture worldwide famous!"
        },
        {
          type: "mc",
          q: "We're from New Zealand. It's in Oceania, ___ the southeast of Australia.",
          options: ["to", "in", "at"],
          a: "to",
          tip: "TO the southeast = toward that direction! Separate countries!",
          successtip: "✓ NEW ZEALAND! 🇳🇿 Separate island nation TO the southeast of Australia! Stunning nature!"
        },
        {
          type: "order",
          q: "Traduce: Mi país está en el oeste, cerca del océano.",
          words: ["My", "country", "is", "in", "the", "west", "near", "the", "ocean"],
          a: "My country is in the west near the ocean",
          tip: "IN the west, NEAR the ocean! Two location phrases!",
          successtip: "✓ MULTIPLE LOCATIONS! Combining direction + proximity! Very complete description!"
        }
      ]
    }
  ],

  // ============================================
  // READING SET (REDUCIDO)
  // ============================================
  readingSet: {
    id: "m6-u13-reading",
    title: "Set 11: Reading Practice - Where We're From",
    category: "reading",
    passage: `Hello! My name is Sofia. I'm Mexican and I'm from Guadalajara, a beautiful city in the west of Mexico. Guadalajara is in the state of Jalisco, near the Pacific coast. It's a big city with great food and music.

My friend Kim is Korean. She's from Busan, a city on the southeast coast of South Korea, by the sea. Busan is the second largest city in Korea. It has beautiful beaches and mountains.

My other friend, Marco, is Italian. He's from a small town in the north of Italy, in the mountains. His town is near Switzerland and it's very cold in winter. He loves skiing.

We all study English because we want to travel and work in different countries. Understanding where people are from helps us connect with the world!`,
    questions: [
      {
        type: "mc",
        q: "Where is Sofia from?",
        options: ["Mexico City", "Guadalajara", "Jalisco"],
        a: "Guadalajara",
        tip: "First paragraph says 'I'm from Guadalajara'!"
      },
      {
        type: "mc",
        q: "Guadalajara is in the ___ of Mexico.",
        options: ["west", "east", "north"],
        a: "west",
        tip: "Text says 'in the west of Mexico'!"
      },
      {
        type: "input",
        q: "What nationality is Kim?",
        a: ["Korean", "korean"],
        tip: "Kim is from South Korea!"
      },
      {
        type: "mc",
        q: "Busan is on the ___.",
        options: ["coast", "mountains", "forest"],
        a: "coast",
        tip: "Text says 'on the southeast coast'!"
      },
      {
        type: "input",
        q: "Marco is from ___. (country)",
        a: ["Italy", "italy"],
        tip: "Second friend from Italy!"
      },
      {
        type: "mc",
        q: "Marco's town is in the ___ of Italy.",
        options: ["north", "south", "east"],
        a: "north",
        tip: "Text says 'in the north of Italy'!"
      },
      {
        type: "mc",
        q: "Where is Marco's town located?",
        options: ["by the sea", "in the mountains", "in the desert"],
        a: "in the mountains",
        tip: "Text says 'in the mountains'!"
      },
      {
        type: "input",
        q: "Marco's town is near ___. (country)",
        a: ["Switzerland", "switzerland"],
        tip: "Last paragraph mentions this country!"
      },
      {
        type: "mc",
        q: "Why do they all study English?",
        options: ["For school", "To travel and work", "For fun"],
        a: "To travel and work",
        tip: "Last paragraph explains why!"
      },
      {
        type: "mc",
        q: "What is the main topic of this text?",
        options: ["Food around the world", "Where people are from", "How to study English"],
        a: "Where people are from",
        tip: "All about origins and locations!"
      }
    ]
  },

  // ============================================
  // REVIEW SET
  // ============================================
  reviewSet: {
    id: "m6-u13-review",
    title: "Set 12: Review - Countries & Locations",
    category: "review",
    exercises: [
      {
        type: "mc",
        q: "I'm from ___ Mexico. (article?)",
        options: ["the", "a", "NO ARTICLE"],
        a: "NO ARTICLE",
        tip: "Most countries NO article! Exception: THE United States!",
        successtip: "✓ NO ARTICLE! Mexico, Canada, France! Remember: THE USA, THE UK only!"
      },
      {
        type: "order",
        q: "Traduce: Soy brasileño y vivo en la costa.",
        words: ["I'm", "Brazilian", "and", "I", "live", "on", "the", "coast"],
        a: "I'm Brazilian and I live on the coast",
        tip: "Brazilian (nationality) + ON the coast!",
        successtip: "✓ COMPLETE! Nationality and location! Perfect introduction!"
      },
      {
        type: "input",
        q: "Complete: She's from China. She's ___.",
        a: ["Chinese", "chinese"],
        tip: "China → Chinese! Nationality adjective!",
        successtip: "✓ CHINESE! Always capitalize nationalities in English!"
      },
      {
        type: "mc",
        q: "Tokyo is ___ Japan. Japan is ___ Asia.",
        options: ["in / in", "on / in", "in / on"],
        a: "in / in",
        tip: "Cities IN countries! Countries IN continents!",
        successtip: "✓ IN / IN! Pattern: City IN country IN continent! Works worldwide!"
      },
      {
        type: "match",
        q: "Match the ERROR to the CORRECTION:",
        pairs: [
          { left: "I'm from the Mexico", right: "I'm from Mexico" },
          { left: "I live in coast", right: "I live on the coast" },
          { left: "I live in an island", right: "I live on an island" }
        ],
        a: "completed",
        tip: "Review common errors from hispanohablantes!",
        successtip: "✓ ERROR CORRECTION! These are the most common mistakes! Now you know better!"
      },
      {
        type: "order",
        q: "Traduce: La isla está frente a la costa de África.",
        words: ["The", "island", "is", "off", "the", "coast", "of", "Africa"],
        a: "The island is off the coast of Africa",
        tip: "OFF the coast OF Africa! Two prepositions!",
        successtip: "✓ OFF THE COAST! Advanced phrase! Shows the island is separated by water!"
      },
      {
        type: "mc",
        q: "Canada is in the ___ of North America.",
        options: ["north", "south", "east"],
        a: "north",
        tip: "Canada is northern! Cold winters!",
        successtip: "✓ NORTH! 🍁 Compass points + locations combined! Geographic knowledge!"
      },
      {
        type: "input",
        q: "Complete: I'm Australian. I'm from ___.",
        a: ["Australia", "australia"],
        tip: "Australian → Australia! Nationality back to country!",
        successtip: "✓ PATTERN REVERSED! Australia → Australian → Australia! You master both directions!"
      },
      {
        type: "mc",
        q: "The restaurant is ___ the museum. (enfrente)",
        options: ["opposite", "between", "behind"],
        a: "opposite",
        tip: "OPPOSITE = enfrente de! Facing each other!",
        successtip: "✓ OPPOSITE! Precise location phrase! You can give clear directions now!"
      },
      {
        type: "input",
        q: "CELEBRATION! You can now talk about countries and locations! Complete: I'm ___ from ___ and I live ___ the ___! (your info)",
        a: ["any", "text", "response", "accepted"],
        tip: "Use your real information! Nationality + country + location!",
        successtip: "✓ UNIT COMPLETE! 🌍🎉 You mastered countries, nationalities, and locations! Now you can describe where you're from, where you live, and understand geography worldwide! Next unit: Numbers and statistics! ¡Excelente trabajo!"
      }
    ]
  }
};
